// Public shop store — pakai axios instance terpisah tanpa auth header.
import axios from 'axios';

function publicURL(path) {
    const base = (axios.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');
    return base + '/api/public' + path;
}

function cartStorageKey(slug) {
    return slug ? `shop_cart_${slug}` : 'shop_cart';
}

function noAuthGet(path, config = {}) {
    return axios.get(publicURL(path), {
        ...config,
        headers: { ...(config.headers || {}), Authorization: '' },
    });
}
function noAuthPost(path, data, config = {}) {
    return axios.post(publicURL(path), data, {
        ...config,
        headers: { ...(config.headers || {}), Authorization: '' },
    });
}

function memberStorageKey(slug) {
    return slug ? `shop_member_${slug}` : 'shop_member';
}

function memberAuthHeaders(token, extra = {}) {
    return { ...extra, Authorization: '', 'X-Shop-Member-Token': token };
}

function noAuthGetWithMember(path, token, config = {}) {
    return axios.get(publicURL(path), {
        ...config,
        headers: memberAuthHeaders(token, config.headers || {}),
    });
}

function noAuthPostWithMember(path, data, token, config = {}) {
    return axios.post(publicURL(path), data, {
        ...config,
        headers: memberAuthHeaders(token, config.headers || {}),
    });
}

export default {
    namespaced: true,
    state: {
        shop: null,
        products: [],
        categories: [],
        currentProduct: null,
        currentOrder: null,
        cart: [],
        cartSlug: '',
        customDomainSlug: '',
    },
    getters: {
        cartTotal: state => state.cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
        cartCount: state => state.cart.reduce((sum, item) => sum + item.quantity, 0),
        memberSession: () => (slug) => {
            try {
                const raw = localStorage.getItem(memberStorageKey(slug));
                return raw ? JSON.parse(raw) : null;
            } catch (_) {
                return null;
            }
        },
    },
    actions: {
        async public_get_shop({ commit }, slug) {
            const r = await noAuthGet('/shop/' + slug);
            commit("SET_SHOP", r.data.data.shop);
            return r;
        },
        async public_get_shop_by_domain({ commit }) {
            const r = await noAuthGet('/shop/by-domain');
            const shop = r.data.data.shop;
            commit("SET_SHOP", shop);
            commit("SET_CUSTOM_DOMAIN_SLUG", shop.slug);
            return r;
        },
        async public_get_products({ commit }, { slug, params }) {
            const r = await noAuthGet('/shop/' + slug + '/products', { params });
            commit("SET_PRODUCTS", r.data.data.products);
            return r;
        },
        async public_get_categories(_, slug) {
            return noAuthGet('/shop/' + slug + '/categories');
        },
        async public_get_product({ commit }, productId) {
            const r = await noAuthGet('/shop/product/' + productId);
            commit("SET_CURRENT_PRODUCT", r.data.data.product);
            return r;
        },
        public_create_order(_, { slug, payload }) {
            return noAuthPost('/shop/' + slug + '/order', payload);
        },
        public_get_payment_link(_, { orderId, method }) {
            return noAuthPost('/shop/order/' + orderId + '/payment', { method });
        },
        public_get_order(_, orderId) {
            return noAuthGet('/shop/order/' + orderId);
        },
        public_get_donation_settings(_, slug) {
            return noAuthGet('/shop/' + slug + '/donation/settings');
        },
        public_get_donation_wall(_, slug) {
            return noAuthGet('/shop/' + slug + '/donation/wall');
        },
        public_create_donation(_, { slug, payload }) {
            return noAuthPost('/shop/' + slug + '/donation', payload);
        },
        public_get_donation_payment(_, { donationNumber, method }) {
            return noAuthPost('/shop/donation/' + donationNumber + '/payment', { method });
        },
        public_get_donation(_, donationNumber) {
            return noAuthGet('/shop/donation/' + donationNumber);
        },
        public_get_lms_settings(_, slug) {
            return noAuthGet('/shop/' + slug + '/lms/settings');
        },
        public_get_lms_courses(_, slug) {
            return noAuthGet('/shop/' + slug + '/lms/courses');
        },
        public_get_lms_course(_, { slug, courseSlug }) {
            return noAuthGet('/shop/' + slug + '/lms/course/' + courseSlug);
        },
        public_create_enrollment(_, { slug, courseSlug, payload }) {
            return noAuthPost('/shop/' + slug + '/lms/course/' + courseSlug + '/enroll', payload);
        },
        public_get_enrollment_payment(_, { enrollmentNumber, method }) {
            return noAuthPost('/shop/lms/enrollment/' + enrollmentNumber + '/payment', { method });
        },
        public_get_enrollment(_, enrollmentNumber) {
            return noAuthGet('/shop/lms/enrollment/' + enrollmentNumber);
        },
        public_get_learning_portal(_, token) {
            return noAuthGet('/shop/lms/access/' + token);
        },
        public_mark_lesson_complete(_, { token, lessonId }) {
            return noAuthPost('/shop/lms/access/' + token + '/lesson/' + lessonId + '/complete', {});
        },
        public_get_shipping_cost(_, { slug, payload }) {
            return noAuthPost('/shop/' + slug + '/shipping-cost', payload);
        },
        public_search_destination(_, { slug, q }) {
            return noAuthGet('/shop/' + slug + '/shipping-destinations', { params: { q } });
        },
        member_login(_, { slug, email, password }) {
            return noAuthPost('/shop/' + slug + '/member/login', { email, password });
        },
        member_forgot_password(_, { slug, email }) {
            return noAuthPost('/shop/' + slug + '/member/forgot-password', { email });
        },
        member_reset_password(_, { slug, token, password }) {
            return noAuthPost('/shop/' + slug + '/member/reset-password', { token, password });
        },
        member_dashboard(_, { slug, token }) {
            return noAuthGetWithMember('/shop/' + slug + '/member/dashboard', token);
        },
        member_logout(_, { slug, token }) {
            return noAuthPostWithMember('/shop/' + slug + '/member/logout', {}, token);
        },
        cart_add({ commit, state }, { slug, product }) {
            if (slug && slug !== state.cartSlug) {
                commit('SWITCH_CART_SLUG', slug);
            }
            const existing = state.cart.find(i => i.product_id === product.product_id && JSON.stringify(i.selected_variants) === JSON.stringify(product.selected_variants));
            if (existing) {
                existing.quantity += product.quantity || 1;
            } else {
                state.cart.push({ ...product, quantity: product.quantity || 1 });
            }
            commit("PERSIST_CART");
        },
        cart_update({ commit, state }, { index, quantity }) {
            if (state.cart[index]) {
                state.cart[index].quantity = quantity;
                if (quantity <= 0) state.cart.splice(index, 1);
            }
            commit("PERSIST_CART");
        },
        cart_remove({ commit, state }, index) {
            state.cart.splice(index, 1);
            commit("PERSIST_CART");
        },
        cart_clear({ commit, state }) {
            state.cart = [];
            commit("PERSIST_CART");
        },
        cart_load({ commit }, slug) {
            commit("LOAD_CART", slug);
        },
    },
    mutations: {
        SET_SHOP(state, shop) { state.shop = shop; },
        SET_CUSTOM_DOMAIN_SLUG(state, slug) { state.customDomainSlug = slug || ''; },
        SET_PRODUCTS(state, products) { state.products = products; },
        SET_CURRENT_PRODUCT(state, p) { state.currentProduct = p; },
        SET_CURRENT_ORDER(state, o) { state.currentOrder = o; },
        SWITCH_CART_SLUG(state, slug) {
            state.cartSlug = slug;
            state.cart = [];
        },
        PERSIST_CART(state) {
            if (!state.cartSlug) return;
            try {
                localStorage.setItem(cartStorageKey(state.cartSlug), JSON.stringify(state.cart));
            } catch (e) { /* noop */ }
        },
        LOAD_CART(state, slug) {
            state.cartSlug = slug || state.cartSlug;
            try {
                const key = cartStorageKey(state.cartSlug);
                const c = localStorage.getItem(key);
                state.cart = c ? JSON.parse(c) : [];
            } catch (e) {
                state.cart = [];
            }
        },
        SET_MEMBER_SESSION(state, { slug, token, member }) {
            localStorage.setItem(memberStorageKey(slug), JSON.stringify({ token, member }));
        },
        CLEAR_MEMBER_SESSION(state, slug) {
            localStorage.removeItem(memberStorageKey(slug));
        },
    },
};
